import { useState, useRef, useEffect } from 'react';
import { LuChevronDown, LuSearch } from 'react-icons/lu';
import Drawer from '../Drawer/Drawer';
import styles from './AddTruckDrawer.module.css';

export default function AddTruckDrawer({
  isOpen,
  onClose,
  drivers = [],
  onAdd,
  mode = 'add',
  initialData = null,
}) {
  const [form, setForm]                     = useState({ truck_id: '', make: '', license_plate: '', capacity: '' });
  const [driverDropdownOpen, setDropOpen]   = useState(false);
  const [driverSearch, setDriverSearch]     = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null); // { id, name }
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState('');
  const dropdownRef = useRef(null);
  const isEditMode = mode === 'edit';

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      truck_id:      initialData?.truck_id || '',
      make:          initialData?.make || '',
      license_plate: initialData?.license_plate || '',
      capacity:      initialData?.capacity != null ? String(initialData.capacity) : '',
    });
    setSelectedDriver(
      initialData?.driver?.id
        ? { id: initialData.driver.id, name: initialData.driver.name || '—' }
        : null
    );
    setDriverSearch('');
    setError('');
  }, [isOpen, initialData]);

  function reset() {
    setForm({ truck_id: '', make: '', license_plate: '', capacity: '' });
    setSelectedDriver(null);
    setDriverSearch('');
    setError('');
  }

  function handleClose() { reset(); onClose(); }

  const filteredDrivers = drivers.filter(d =>
    d.name.toLowerCase().includes(driverSearch.toLowerCase())
  );

  const isValid = form.truck_id.trim() !== '' && form.make.trim() !== '';

  async function handleSubmit() {
    if (!isValid) return;
    setSubmitting(true);
    setError('');
    try {
      await onAdd({
        truck_id:      form.truck_id,
        make:          form.make,
        license_plate: form.license_plate || null,
        capacity:      form.capacity ? parseFloat(form.capacity) : null,
        driver:        selectedDriver?.id || null,
      });
      reset();
    } catch (err) {
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'add'} truck`);
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <button
      className={`${styles.submitBtn} ${!isValid || submitting ? styles.submitBtnDisabled : ''}`}
      disabled={!isValid || submitting}
      onClick={handleSubmit}
    >
      {submitting ? (isEditMode ? 'Saving…' : 'Adding…') : (isEditMode ? 'Save changes' : 'Add new truck')}
    </button>
  );

  return (
    <Drawer isOpen={isOpen} title={isEditMode ? 'Edit truck' : 'Add truck'} onClose={handleClose} footer={footer}>
      <div className={styles.formField}>
        <label className={styles.label}>ID</label>
        <input
          className={styles.input}
          placeholder="e.g. Truck #55"
          value={form.truck_id}
          onChange={e => setForm(f => ({ ...f, truck_id: e.target.value }))}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.label}>Make</label>
        <input
          className={styles.input}
          placeholder="e.g. Volvo FH"
          value={form.make}
          onChange={e => setForm(f => ({ ...f, make: e.target.value }))}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.label}>Number plate</label>
        <input
          className={styles.input}
          placeholder="e.g. GR-1234-20"
          value={form.license_plate}
          onChange={e => setForm(f => ({ ...f, license_plate: e.target.value }))}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.label}>Capacity (MT)</label>
        <input
          className={styles.input}
          type="number"
          placeholder="e.g. 50"
          value={form.capacity}
          onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
        />
      </div>

      <div className={styles.formField} ref={dropdownRef}>
        <label className={styles.label}>Assign driver</label>
        <button
          className={styles.selectTrigger}
          onClick={() => setDropOpen(o => !o)}
          type="button"
        >
          <span className={selectedDriver ? styles.selectValue : styles.selectPlaceholder}>
            {selectedDriver?.name || 'Select a driver'}
          </span>
          <LuChevronDown size={14} color="#6b7280" />
        </button>

        {driverDropdownOpen && (
          <div className={styles.dropdownPanel}>
            <div className={styles.dropdownSearch}>
              <LuSearch size={13} color="#9ca3af" />
              <input
                className={styles.dropdownSearchInput}
                placeholder="Search driver..."
                value={driverSearch}
                onChange={e => setDriverSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className={styles.dropdownList}>
              {filteredDrivers.length === 0 ? (
                <p className={styles.noResults}>No drivers found</p>
              ) : (
                filteredDrivers.map(d => (
                  <button
                    key={d.id}
                    className={`${styles.dropdownOption} ${selectedDriver?.id === d.id ? styles.dropdownOptionActive : ''}`}
                    onClick={() => { setSelectedDriver(d); setDropOpen(false); setDriverSearch(''); }}
                    type="button"
                  >
                    {d.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p style={{ color: 'var(--red-color)', fontSize: 13, marginTop: 8, fontFamily: '"General Sans", sans-serif' }}>{error}</p>}
    </Drawer>
  );
}
